import { useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';

import { _isBlank, _isNil, _isNotBlank, _notNil } from '@/littledash';

import type { InlineEditInputProps } from './InlineEditInput.model';

const InlineEditInput = <T extends string>({
  value,
  propName,
  change,
  validate,
  className = '',
  classInvalid = '',
  isDisabled = false,
  placeholder = '',
  testId = 'inline-edit-input',
}: InlineEditInputProps<T>) => {
  const displayRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayValue = _isNotBlank(value) ? value : placeholder;

  const setInvalidState = useCallback(
    (invalid: boolean) => {
      if (_notNil(inputRef.current) && _isNotBlank(classInvalid)) {
        if (invalid) {
          inputRef.current.classList.add(classInvalid);
        } else {
          inputRef.current.classList.remove(classInvalid);
        }
      }
    },
    [classInvalid]
  );

  const openEditor = useCallback(() => {
    if (isDisabled) {
      return;
    }
    if (_notNil(displayRef.current)) {
      displayRef.current.style.display = 'none';
    }
    if (_notNil(inputRef.current)) {
      inputRef.current.style.display = '';
      inputRef.current.value = value;
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isDisabled, value]);

  const closeEditor = useCallback(() => {
    setInvalidState(false);
    if (_notNil(inputRef.current)) {
      inputRef.current.style.display = 'none';
    }
    if (_notNil(displayRef.current)) {
      displayRef.current.style.display = '';
    }
  }, [setInvalidState]);

  const handleBlur = useCallback(() => {
    const input = inputRef.current;
    if (_isNil(input)) {
      return;
    }
    const draftValue = input.value;
    if (_isBlank(draftValue) || draftValue === value) {
      input.value = value;
      closeEditor();
      return;
    }
    if (_notNil(validate) && !validate(draftValue)) {
      setInvalidState(true);
      requestAnimationFrame(() => input.focus());
      return;
    }
    closeEditor();
    change({ [propName]: draftValue } as Record<T, string>);
  }, [change, closeEditor, propName, setInvalidState, validate, value]);

  const handleInput = useCallback(() => {
    if (_notNil(validate) && _notNil(inputRef.current)) {
      const draftValue = inputRef.current.value;
      setInvalidState(_isNotBlank(draftValue) && !validate(draftValue));
    }
  }, [setInvalidState, validate]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        inputRef.current?.blur();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        if (_notNil(inputRef.current)) {
          inputRef.current.value = value;
        }
        setInvalidState(false);
        inputRef.current?.blur();
      }
    },
    [setInvalidState, value]
  );

  useEffect(() => {
    if (_notNil(inputRef.current) && document.activeElement !== inputRef.current) {
      inputRef.current.value = value;
    }
  }, [value]);

  return (
    <>
      <span
        ref={displayRef}
        className={cn('inline-flex items-center', className)}
        onClick={openEditor}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            openEditor();
          }
        }}
        role={isDisabled ? undefined : 'button'}
        tabIndex={isDisabled ? -1 : 0}
        data-test-component="InlineEditInput"
        data-test-element="display"
        data-testid={`${testId}-display`}
      >
        <span className={_isNotBlank(displayValue) ? '' : 'gray'}>{displayValue}</span>
      </span>
      <input
        ref={inputRef}
        defaultValue={value}
        style={{ display: 'none' }}
        onBlur={handleBlur}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        className={className}
        disabled={isDisabled}
        data-test-component="InlineEditInput"
        data-test-element="input"
        data-testid={testId}
        aria-label={propName}
      />
    </>
  );
};

export default InlineEditInput;
